import React, { useState, useCallback } from 'react';
import { FileText, Camera, Eye, Download, Trash2 } from 'lucide-react';
import FileUpload from '../../../common/FileUpload';
import PhotoAttachment from '../../../common/PhotoAttachment';
import { formatFileSize } from '../../../../utils/fileUpload';

/**
 * DocumentsTab Component
 *
 * Displays and manages documents for equipment items.
 * Extracted from EquipmentDetailView for better file size management.
 *
 * FT-345 (2026-07-08): added a Photos section powered by <PhotoAttachment>
 * (FT-343). Photos live on `equipment.photos: PhotoRef[]` — the legacy
 * `documents` array is unchanged and still handles PDFs / Word docs. Images
 * dragged into the old FileUpload area are rejected with a redirect message
 * from useFileUpload (see FT-345 CODER report §FILE_SIZE_LIMITS fix).
 */
const DocumentsTab = ({
  documents = [],
  uploading = false,
  onUpload,
  onPreview,
  onDownload,
  onDelete,
  onRename,
  // FT-345 — photo evidence wiring
  equipment = null,
  vesselId,
  capturedBy,
  onUpdateEquipment
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState('');

  const handleStartRename = (doc) => {
    setEditingId(doc.id);
    setEditedName(doc.fileName);
  };

  const handleSaveRename = (docId) => {
    if (editedName.trim() && onRename) {
      onRename(docId, editedName.trim());
    }
    setEditingId(null);
    setEditedName('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditedName('');
  };

  // FT-345: photo change handler routes back through the parent's equipment
  // update flow so the new `equipment.photos` field is persisted alongside
  // every other field on the record.
  const handlePhotosChange = useCallback((nextPhotos) => {
    if (typeof onUpdateEquipment !== 'function' || !equipment) return;
    onUpdateEquipment({ ...equipment, photos: nextPhotos });
  }, [onUpdateEquipment, equipment]);

  const photos = Array.isArray(equipment?.photos) ? equipment.photos : [];
  const canShowPhotos = Boolean(equipment?.id && vesselId);

  return (
    <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
      <div className="bg-slate-700 px-5 py-3">
        <h3 className="text-base font-semibold text-white">Documents & Certificates</h3>
      </div>
      <div className="px-3 md:px-4 py-3 md:py-4">
        <p className="mb-3 md:mb-4 text-xs text-slate-600">
          Upload certificates, manuals, service records, and photos
        </p>

      {/* FT-345: Photos section — new photo evidence pipeline (FT-340) */}
      {canShowPhotos && (
        <div className="mb-6" data-testid="documents-tab-photos-section">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="h-4 w-4 text-slate-600" />
            <h4 className="text-[13px] font-medium text-slate-800">Photos</h4>
          </div>
          <p className="mb-2 text-[11px] text-slate-500">
            Capture photos of this equipment. Photos sync across your devices.
          </p>
          <PhotoAttachment
            recordType="equipment_document"
            recordId={equipment.id}
            vesselId={vesselId}
            capturedBy={capturedBy}
            photos={photos}
            onChange={handlePhotosChange}
            maxCount={20}
            caption={true}
            emptyLabel="No photos yet. Tap Add photo to capture."
            testId="equipment-photos"
            // FT-565: equipment `photos` is list-merged by sync
            // (sync/mergeListFields.js), so deletes MUST tombstone the ref
            // instead of splicing it out — otherwise a concurrent edit on
            // another device would resurrect the deleted photo.
            tombstoneOnDelete
          />
        </div>
      )}

      {/* File Upload Component — PDFs / Word documents only (images rejected). */}
      <div className="mb-6">
        <FileUpload
          label="Upload Document"
          accept=".pdf,.doc,.docx"
          maxSizeMB={2}
          onUpload={onUpload}
          disabled={uploading}
        />
      </div>

      {/* Uploaded Documents List */}
      {documents.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-[13px] font-medium text-slate-800">
            Uploaded Documents ({documents.length})
          </h4>
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {editingId === doc.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(doc.id);
                            if (e.key === 'Escape') handleCancelRename();
                          }}
                          onBlur={() => handleSaveRename(doc.id)}
                          autoFocus
                          className="text-[13px] font-medium text-slate-900 border border-cyan-500 rounded px-2 py-1 flex-1 min-w-0"
                        />
                      </div>
                    ) : (
                      <p
                        className="text-[13px] font-medium text-slate-900 truncate cursor-text"
                        onDoubleClick={() => handleStartRename(doc)}
                        title="Double-click to rename"
                      >
                        {doc.fileName}
                      </p>
                    )}
                    <div className="flex items-center space-x-2 text-[11px] text-slate-500">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      {doc.uploadedAt && (
                        <>
                          <span>•</span>
                          <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  <button
                    onClick={() => onPreview(doc)}
                    className="p-2 text-slate-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDownload(doc)}
                    className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(doc.id)}
                    className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-[12px] text-slate-500">
            No documents uploaded yet
          </p>
        </div>
      )}
      </div>
    </section>
  );
};

export default DocumentsTab;
